import type { Awaited } from '../../../types/typescript';
import searchHotels from '../hotel/searchHotels';

type Hotel = Awaited<ReturnType<typeof searchHotels>>['data'][0];
type Campaign = NonNullable<Hotel['webCampaigns']>[0];
type CampaignPrompt = NonNullable<Hotel['webCampaignPrompts']>[0];

// check if any stay dates get overlap with valid stay priod. This function is from BFF to have same logic on both client and server.
export const checkAnyStayDatesGetOverlapWithValidStayPeriod = (
  campaign: Campaign | CampaignPrompt | undefined,
  checkInDate: Date,
  checkOutDate: Date
) => {
  if (!campaign) return false;

  const { validStayDateFrom = '', validStayDateTo = '' } = campaign;

  // if both validStayDateFrom and validStayDateTo are not set in strapi, return true for any dates to get discount
  if (!validStayDateFrom && !validStayDateTo) return true;

  const campaignStayPeriodStart = new Date(validStayDateFrom.replace(/-/g, '/'));
  const campaignStayPeriodEnd = new Date(validStayDateTo.replace(/-/g, '/'));

  //                           StayPeriodEnd
  //                                |
  //  <=============================|
  //          |--OK--|   |--OK--|   |--OK--|
  //                                 |--NG--|
  if (!validStayDateFrom) return checkInDate <= campaignStayPeriodEnd;

  //   StayPeriodStart
  //          |
  //          |=============================>
  //    |--OK--|   |--OK--|   |--OK--|
  //   |--NG--|
  if (!validStayDateTo) return checkOutDate > campaignStayPeriodStart;

  // there are 5 possible overlapping cases
  //   StayPeriodStart        StayPeriodEnd
  //      |                            |
  //      |============================|
  //      |-------------1--------------|
  //          |---------2----------|
  //   |----------------3----------------|
  //  |---4---|                    |---5---|
  if (checkInDate >= campaignStayPeriodStart && checkOutDate <= campaignStayPeriodEnd) return true; // case 1 and 2
  if (checkInDate <= campaignStayPeriodStart && checkOutDate >= campaignStayPeriodEnd) return true; // case 1 and 3
  if (checkInDate < campaignStayPeriodStart && checkOutDate > campaignStayPeriodStart) return true; // case 4
  if (checkInDate <= campaignStayPeriodEnd && checkOutDate > campaignStayPeriodEnd) return true; // case 5

  return false;
};

/**
 * Returns a discounted price calculated with the campaign
 *
 * @param guestRateTaxIncl - guest rate
 * @param campaign - target campaign
 * @return discounted price
 */
export const calculateDiscountedPrice = (
  guestRateTaxIncl: number,
  campaign: Campaign,
  effectiveDate?: string
) => {
  const { discount = 0, maxDayDiscount } = campaign;
  const discountAmount = Math.floor(guestRateTaxIncl * Number((discount * 0.01).toFixed(2)));
  const minimumWeekdayPrice = Number(campaign.minimumWeekdayPrice);
  const minimumWeekendPrice = Number(campaign.minimumWeekendPrice);

  if (effectiveDate && minimumWeekdayPrice && minimumWeekendPrice) {
    // Saturdays are considered as holiday
    const isWeekend = new Date(effectiveDate).getDay() === 6;

    // if it's holiday and original price is less than weekend minimum requirement, then do nothing and return the original rate
    if (isWeekend) {
      if (guestRateTaxIncl < minimumWeekendPrice) return guestRateTaxIncl;
    } else if (guestRateTaxIncl < minimumWeekdayPrice) {
      // if it's weekday and original price is less than weekday minimum requirement, then do nothing and return the original rate
      return guestRateTaxIncl;
    }
  }

  // if actual discountAmount is higher than maxDiscount, subtract the maxDiscount from original rate
  if (maxDayDiscount && maxDayDiscount < discountAmount) {
    return guestRateTaxIncl - maxDayDiscount;
  }

  // otherwise, subtract the discountAmount
  return guestRateTaxIncl - discountAmount;
};
